import { useAlternativeRegistrationModal } from '@exam-center/alternative-registration';
import { EKycVerificationStatus, ExamineeRegistrationStatus } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate, useParams } from '@exam-center/router';
import { useTimeGate } from '@exam-center/server-time';
import {
  Box,
  EditOutlinedIcon,
  VisibilityOutlinedIcon,
  type BoxProps,
  MoreMenuIconButton,
} from '@shared/components';
import { usePermission } from '@shared/permissions';
import { dayjs } from '@shared/utils';
import { useMemo } from 'react';

interface MoreActionButtonProps extends BoxProps {
  examEventExamineeId: string;
  status: ExamineeRegistrationStatus;
  examEventStartedAt: Date;
  eKycStatus: EKycVerificationStatus;
}

export default function MoreActionsButton(props: Readonly<MoreActionButtonProps>) {
  const { examCenterCode, examEventId } = useParams(
    '/:examCenterCode/proctoring/exam-events/:examEventId/registrations',
  );
  const { permissions } = usePermission();
  const { examEventExamineeId, status, eKycStatus, examEventStartedAt, sx, ...restProps } = props;

  const examDayStart = useMemo(
    () => dayjs(examEventStartedAt).startOf('day').toDate(),
    [examEventStartedAt],
  );

  const examDayEnd = useMemo(
    () => dayjs(examEventStartedAt).endOf('day').toDate(),
    [examEventStartedAt],
  );

  const thirtyMinAfterStart = useMemo(
    () => dayjs(examEventStartedAt).add(30, 'minute').toDate(),
    [examEventStartedAt],
  );

  const isOnOrAfterExamDay = useTimeGate(examDayStart);
  const isPastExamDay = useTimeGate(examDayEnd);
  const isMoreThan30MinutesAfterExamStartTime = useTimeGate(thirtyMinAfterStart);
  const isOnExamDate = isOnOrAfterExamDay && !isPastExamDay;

  const navigate = useNavigate();

  const { setAction } = useAlternativeRegistrationModal();

  const handleSeeMoreInfo = () => {
    navigate(
      '/:examCenterCode/proctoring/exam-events/:examEventId/registrations/:examEventExamineeId',
      {
        replace: true,
        params: {
          examCenterCode,
          examEventId,
          examEventExamineeId,
        },
      },
    );
  };

  const handleRegisterWithOtherDocuments = () => {
    setAction({
      type:
        eKycStatus === EKycVerificationStatus.NotRequired
          ? 'register-foreigner-examinee-with-alternative-document'
          : 'register-examinee-with-alternative-document',
      examEventExamineeId: examEventExamineeId ?? '',
    });
  };

  const baseMenuOptions = [
    {
      icon: <VisibilityOutlinedIcon />,
      label: 'ดูรายละเอียด',
      onClick: handleSeeMoreInfo,
      requiredPermission: [APP_PERMISSIONS.PROCTORING.REGISTRATION.VIEW],
    },
    {
      icon: <EditOutlinedIcon />,
      label: 'ลงทะเบียนด้วยเอกสารอื่น',
      onClick: handleRegisterWithOtherDocuments,
      hidden: !(
        status === ExamineeRegistrationStatus.NotRegistered &&
        isOnExamDate &&
        !isMoreThan30MinutesAfterExamStartTime
      ),
      requiredPermission: [APP_PERMISSIONS.PROCTORING.REGISTRATION.UPDATE],
    },
  ];

  const filteredOptions = baseMenuOptions
    .filter(
      (option) =>
        !option.hidden &&
        option.requiredPermission.every((reqPermission) => permissions.includes(reqPermission)),
    )
    .map((option) => ({
      icon: option.icon,
      label: option.label,
      onClick: option.onClick,
    }));

  return (
    <Box sx={{ mx: 'auto', ...sx }} {...restProps}>
      {filteredOptions.length > 0 ? <MoreMenuIconButton options={filteredOptions} /> : null}
    </Box>
  );
}
